// Catálogo estático de conectores do embarcador (H3, PO1-3261). Os campos são
// parâmetros do catálogo EMBARC (mesmo formulário de GET /parametros/embarcador/
// formulario); por isso eles NÃO aparecem na tela de Parâmetros (montarAssuntos os
// oculta) e só são editados aqui. `campoAtivador` = o parâmetro cujo preenchimento
// significa "integração ativa"; desativar = gravar '' nele (o backend apaga a linha).
// `rotulos` dá label/ajuda em português para cada campo (fallback: descricao/nome).
// `tipoSistema` e `capacidades` (H4, PO1-3617) são a classificação escrita na tela
// para os filtros "Tipo de sistema" e "O que troca" funcionarem com os 6 cards até
// eles migrarem para o catálogo (valores da lista padronizada da H1: erp | fiscal |
// captura | coleta; pedido | nfe | cte | tracking | contas_pagar). O rótulo
// `categoria` exibido não muda.
import { getNormalizedValue, normalizeType } from "../Parametros/renderControl";

export const DIRECOES = { ENTRADA: "Entrada", SAIDA: "Saída", BIDIRECIONAL: "Bidirecional" };

export const TEXTO_GERENCIADO = {
  titulo: "Configurado pela equipe Everlog",
  texto: "Abra um chamado para alterar.",
};

export const LINK_CERTIFICADO = "/app/certificado-digital";

export const CONECTORES = [
  // PO1-3655: o card fixo "Protheus (versão antiga)" (importador por endpoint do
  // cliente, parâmetros 46 a 50) saiu daqui. A versão antiga agora é a v0.9 do
  // conector "TOTVS Protheus" do catálogo de ERPs (card catalogo:totvs-protheus),
  // escolhida no seletor de versão. Os parâmetros 46 a 50 continuam ocultos na
  // tela de Parâmetros pelo prefixo "protheus" (montarAssuntos).
  {
    key: "espiao",
    nome: "Espião NF-e",
    sigla: "ES",
    categoria: "Captura de NF-e",
    tipoSistema: "captura",
    capacidades: ["nfe"],
    direcao: DIRECOES.ENTRADA,
    descricaoCurta: "Captura as notas emitidas contra o seu CNPJ.",
    descricaoLonga:
      "Serviço de captura de documentos fiscais: o Hub consulta o Espião NF-e e traz as notas e CT-es emitidos para os CNPJs da conta, sem o fornecedor precisar enviar nada. Os tokens vêm do painel do Espião.",
    campos: ["espTokenConta", "espTokenUsuario", "espUrlBase", "espJanelaDias", "espModelos"],
    // Nomes novos com o prefixo (ou do módulo ESPIAONFE) entram sozinhos na lista.
    prefixo: "esp",
    modulo: "ESPIAONFE",
    campoAtivador: "espTokenConta",
    avisos: ["Os CNPJs capturados são os cadastrados na conta do Espião NF-e; confira lá se todas as filiais estão incluídas."],
    gerenciado: false,
    rotulos: {
      espTokenConta: { label: "Token da conta", ajuda: "Token da conta no Espião NF-e. Preenchido = integração ativa. Nunca é exibido depois de salvo." },
      espTokenUsuario: { label: "Token do usuário", ajuda: "Token do usuário de API gerado no painel do Espião NF-e." },
      espUrlBase: { label: "Endereço da API", ajuda: "URL base da API do Espião NF-e. Só altere se o suporte do Espião pedir." },
      espJanelaDias: { label: "Janela de captura (dias)", ajuda: "Quantos dias para trás cada consulta busca documentos novos." },
      espModelos: { label: "Modelos de documento", ajuda: "Modelos capturados, separados por vírgula (55 = NF-e, 57 = CT-e)." },
    },
  },
  {
    key: "sefaz",
    nome: "SEFAZ (captura de CT-e)",
    sigla: "SZ",
    categoria: "Fisco",
    tipoSistema: "fiscal",
    capacidades: ["cte"],
    direcao: DIRECOES.ENTRADA,
    descricaoCurta: "Baixa na SEFAZ os CT-es emitidos contra você.",
    descricaoLonga:
      "Com o certificado digital A1 do embarcador, o Hub consulta a SEFAZ (distribuição de DF-e) e baixa os CT-es em que você é tomador, direto da fonte. Fica ativa enquanto houver um certificado válido enviado em Certificado Digital.",
    campos: ["sefazUrlBase", "sefazTpAmb", "sefazCUFAutor"],
    campoAtivador: null,
    usaCertificado: true,
    avisos: ["A ativação é pelo certificado, não por um campo: envie ou renove o A1 em Certificado Digital.", "Ambiente de homologação não traz documentos reais; use só em testes combinados com o suporte."],
    gerenciado: false,
    rotulos: {
      sefazUrlBase: { label: "Endereço do serviço", ajuda: "URL do web service de distribuição da SEFAZ. Só altere se o suporte pedir." },
      sefazTpAmb: { label: "Ambiente", ajuda: "Produção (documentos reais) ou Homologação (testes)." },
      sefazCUFAutor: { label: "UF do autor (código IBGE)", ajuda: "Código IBGE da UF do embarcador (ex.: 35 = SP), exigido pela SEFAZ na consulta." },
    },
  },
  {
    key: "obc",
    nome: "Oracle",
    sigla: "OR",
    categoria: "ERP",
    tipoSistema: "erp",
    capacidades: ["pedido"],
    direcao: DIRECOES.SAIDA,
    descricaoCurta: "Envia os pedidos de compra e as faturas ao Oracle.",
    descricaoLonga:
      "O Hub envia os pedidos de compra e as faturas ao Oracle em nome do solicitante informado. O endereço e o token do serviço são configurados pela Everlog; aqui fica só o usuário solicitante.",
    campos: ["obcUsuario"],
    campoAtivador: "obcUsuario",
    avisos: ["O usuário do Oracle por transportadora é definido em Parâmetros › Transportadora › Oracle."],
    gerenciado: false,
    rotulos: {
      obcUsuario: { label: "E-mail do solicitante", ajuda: "Usuário do Oracle em nome de quem os pedidos são enviados. Preenchido = integração ativa." },
    },
  },
  {
    key: "neogrid",
    nome: "Neogrid",
    sigla: "NG",
    categoria: "Captura de NF-e e CT-e",
    tipoSistema: "captura",
    capacidades: ["nfe", "cte"],
    direcao: DIRECOES.ENTRADA,
    descricaoCurta: "Traz as notas e os CT-es do embarcador pela Neogrid.",
    descricaoLonga:
      "O Hub consulta a Neogrid (portal fiscal TOTVS) e traz as NF-e de saída e os CT-es do embarcador. As credenciais OAuth e os endereços são configurados pela equipe Everlog.",
    campos: [],
    campoAtivador: null,
    // Gerenciado, mas mostra "Ativa" para quem tem a URL de NF-e configurada (hoje, a Hengst).
    ativoQuandoCampo: "neoUrlNFe",
    avisos: [],
    gerenciado: true,
    rotulos: {},
  },
  {
    key: "v360",
    nome: "V360",
    sigla: "V3",
    categoria: "Portal de fretes",
    tipoSistema: "captura",
    capacidades: ["cte"],
    direcao: DIRECOES.BIDIRECIONAL,
    descricaoCurta: "Troca de CT-es e status com o portal V360.",
    descricaoLonga:
      "Integração com o portal de fretes V360: o Hub busca os CT-es no portal e devolve o status de liberação. A configuração é feita pela equipe Everlog.",
    campos: [],
    campoAtivador: null,
    // A integração é específica da Domino's (URL e acesso próprios no EverlogAPI_Clientes).
    embarcadoresAtivos: [77],
    avisos: [],
    gerenciado: true,
    rotulos: {},
  },
];

export const CONECTORES_POR_KEY = CONECTORES.reduce((acc, c) => {
  acc[c.key] = c;
  return acc;
}, {});

function nomeDe(p) {
  return String((p && p.nome) || "");
}

// Parâmetros do formulário que pertencem ao conector, na ordem do catálogo
// estático; depois, os que casam por prefixo/módulo e ainda não entraram.
export function camposDoConector(conector, formulario) {
  const lista = Array.isArray(formulario) ? formulario : [];
  const fixos = conector.campos || [];
  const saida = [];
  const vistos = {};
  fixos.forEach((nome) => {
    const p = lista.find((item) => nomeDe(item) === nome);
    if (p && !vistos[nome]) {
      vistos[nome] = true;
      saida.push(p);
    }
  });
  if (conector.prefixo || conector.modulo) {
    lista.forEach((p) => {
      const nome = nomeDe(p);
      if (!nome || vistos[nome]) return;
      const porPrefixo = conector.prefixo && nome.indexOf(conector.prefixo) === 0;
      const porModulo = conector.modulo && String(p.modulo || "").toUpperCase() === String(conector.modulo).toUpperCase();
      if (porPrefixo || porModulo) {
        vistos[nome] = true;
        saida.push(p);
      }
    });
  }
  return saida;
}

export function rotuloDoCampo(conector, param) {
  const def = (conector.rotulos || {})[param.nome];
  if (def) return { label: def.label || param.nome, ajuda: def.ajuda || "" };
  // Sem rótulo escrito: nome técnico como label e a descrição do catálogo como ajuda.
  return { label: param.nome, ajuda: param.descricao || "" };
}

// "Preenchido" na convenção do formulário: senha conta pelo hasValue (o valor
// nunca vem), os demais pelo valor não vazio.
export function campoPreenchido(param) {
  if (!param) return false;
  if (normalizeType(param.tipo) === "password") return !!param.hasValue;
  return getNormalizedValue(param.valor).trim() !== "";
}
